import React, { useEffect, useState, useCallback } from 'react'
import { cn } from '@/lib/utils'
import { DrawerHeader } from './DrawerHeader'
import { useDrawer, type DrawerConfig } from './DrawerProvider'
import { useConfirm } from '@/hooks/useConfirm'
import { useDrawerKeyboard } from '@/hooks/useDrawerKeyboard'

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  full: 'max-w-full',
  '90': 'w-[90%]',
  '80': 'w-[80%]',
  '70': 'w-[70%]',
  '60': 'w-[60%]',
  '50': 'w-[50%]',
}

interface DrawerProps extends DrawerConfig {
  isOpen: boolean
  isFirst?: boolean
  stackIndex?: number
  pushAmount?: number
  totalDrawers?: number
  isTopmost?: boolean
}

export function Drawer({ 
  id,
  title,
  content,
  footer,
  size = 'md',
  onClose,
  closeOnOutsideClick = true,
  showCloseButton = true,
  headerActions,
  isOpen,
  isFirst = false,
  stackIndex = 0,
  pushAmount = 0,
  isTopmost = false,
  hasUnsavedChanges = false,
  confirmClose = false,
  confirmCloseMessage,
  loadingState = 'idle',
}: DrawerProps) {
  const { closeDrawer } = useDrawer()
  const [isVisible, setIsVisible] = useState(false)
  const [showSuccess] = useState(false)
  const { confirm, ConfirmDialog } = useConfirm()
  
  // Handle opening animation
  useEffect(() => {
    if (isOpen) {
      // Force a reflow to ensure the transition works
      requestAnimationFrame(() => {
        setIsVisible(true)
      })
    } else {
      setIsVisible(false)
    }
  }, [isOpen])
  
  const handleClose = useCallback(() => {
    // Check if we need to confirm before closing
    if (hasUnsavedChanges && confirmClose) {
      confirm({
        title: 'Unsaved Changes',
        message: confirmCloseMessage || 'You have unsaved changes. Are you sure you want to close?',
        confirmLabel: 'Close without saving',
        cancelLabel: 'Continue editing',
        variant: 'destructive',
        onConfirm: () => {
          setIsVisible(false)
          // Delay the actual close to allow animation to complete
          setTimeout(() => {
            closeDrawer(id)
            onClose?.()
          }, 300)
        },
      })
    } else {
      setIsVisible(false)
      // Delay the actual close to allow animation to complete
      setTimeout(() => {
        closeDrawer(id)
        onClose?.()
      }, 300)
    }
  }, [hasUnsavedChanges, confirmClose, confirmCloseMessage, confirm, id, closeDrawer, onClose])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnOutsideClick && e.target === e.currentTarget) {
      handleClose()
    }
  }

  // Handle escape key
  useDrawerKeyboard(id, {
    onClose: isTopmost ? handleClose : undefined,
  })

  // Handle success animation
  // const handleSuccessAndClose = useCallback(async () => {
  //   setShowSuccess(true)
  //   await new Promise(resolve => setTimeout(resolve, 1000))
  //   handleClose()
  // }, [handleClose])

  if (!isOpen) return null

  return (
    <>
      <ConfirmDialog />
      {/* Backdrop for each drawer */}
      <div
        className={cn(
          "fixed inset-0 transition-opacity duration-300",
          // Graduated opacity for stacked drawers - darker in dark mode
          isFirst ? "bg-black/80 dark:bg-black/90" : stackIndex === 1 ? "bg-black/40 dark:bg-black/60" : "bg-black/20 dark:bg-black/40",
          // Add subtle backdrop blur
          "backdrop-blur-[2px]",
          isVisible ? "opacity-100" : "opacity-0"
        )}
        style={{ 
          zIndex: 9998 + stackIndex 
        }}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      
      {/* Drawer */}
      <div
        className={cn(
          "fixed inset-y-0 right-0 flex flex-col",
          "bg-card text-card-foreground",
          "transition-transform duration-300 ease-in-out",
          // Enhanced border and shadow for depth
          !isFirst ? "border-l-4 border-border/50" : "border-l border-border",
          !isFirst ? "shadow-[0_0_50px_rgba(0,0,0,0.3)]" : "shadow-2xl",
          sizeClasses[size as keyof typeof sizeClasses],
          // Add depth effect class for pushed drawers
          pushAmount > 0 && "drawer-stack-effect"
        )}
        style={{ 
          zIndex: 9999 + stackIndex,
          // Transform for slide and push effects
          transform: isVisible 
            ? pushAmount > 0 
              ? `translateX(-${pushAmount}px)` 
              : 'translateX(0)'
            : 'translateX(100%)',
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`drawer-title-${id}`}
      >
        {/* Overlay for pushed drawers */}
        {pushAmount > 0 && isVisible && (
          <>
            <div className="drawer-overlay" />
            <div className="drawer-edge-highlight" />
          </>
        )}
        
        {/* Success overlay */}
        {showSuccess && (
          <div className="absolute inset-0 bg-green-50/90 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm font-medium text-green-900">Saved successfully!</p>
            </div>
          </div>
        )}
        
        {/* Fixed Header */}
        <DrawerHeader
          title={title}
          onClose={handleClose}
          showCloseButton={showCloseButton}
          actions={headerActions}
          className={cn(
            "relative z-10",
            pushAmount > 0 && "pl-8"
          )}
        />
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto bg-muted/30 p-6 relative z-10">
          {loadingState === 'loading' ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : loadingState === 'error' ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div className="text-destructive">
                <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm text-muted-foreground">Failed to load content</p>
            </div>
          ) : (
            content
          )}
        </div>
        
        {/* Fixed Footer */}
        {footer && (
          <div className="border-t border-border bg-card relative z-10">
            {footer}
          </div>
        )}
      </div>
    </>
  )
}

// Drawer container that renders all drawers from context
export function DrawerContainer() {
  const { drawers } = useDrawer()
  const totalDrawers = drawers.length
  
  return (
    <>
      {drawers.map((drawer, index) => {
        // Calculate push amount for stacking effect
        const pushAmount = (totalDrawers - 1 - index) * 30 // 30px per drawer
        
        return (
          <Drawer
            key={drawer.id}
            {...drawer}
            isOpen={true}
            isFirst={index === 0}
            stackIndex={index}
            pushAmount={pushAmount}
            totalDrawers={totalDrawers}
            isTopmost={index === totalDrawers - 1}
          />
        )
      })}
    </>
  )
}